import {shallowRef, computed, watch, watchEffect} from 'vue'
import compareSemverVersionNumbers from 'shared/utils/compareSemverVersionNumbers'
import {useSessionStore} from 'shared/stores/session'
import {useStorage} from '@vueuse/core'

export const useSessionNetworkRequestsFilter = ({processedRequests, displayedColumns}) => {
	const sessionStore = useSessionStore()

	const allRequests = shallowRef(processedRequests)
	const displayedColumnsRef = shallowRef(displayedColumns)

	const filterOptions = useStorage(
		'session-network-filter-options',
		{
			search: '',
			searchTerms: [],
			invert: false,
			hideDataUrls: false,
			hideExtensionUrls: false,
			errorsOnly: false,
			thirdPartyRequests: false,
			requestType: 'all',
		},
		localStorage,
		{mergeDefaults: true}
	)

	watchEffect(() => {
		filterOptions.value.searchTerms = filterOptions.value.search.toLowerCase().trim().split(' ').filter(Boolean)
	})

	const REQUEST_TYPES = {
		html: 'HTML',
		css: 'CSS',
		script: 'JS',
		xmlhttprequest: 'Fetch/XHR',
		font: 'Fonts',
		image: 'Images',
		media: 'Media',
		websocket: 'WS',
		other: 'Other',
	}

	// Before BEB-2223, `XMLHttpRequest` and `fetch` requests were always `xmlhttprequest`.
	// Now these types are differentiated with "xmlhttprequest" and "fetch"
	function normalizeRequestType(requestType) {
		if (!requestType) return 'other'
		return requestType
			.toLowerCase()
			.replace('fetch', 'xmlhttprequest')
			.replace('ping', 'other')
			.replace('main_frame', 'html')
	}

	const requestTypeFilters = computed(() => {
		if (!allRequests.value) return []
		const requestTypesOfSession = new Set(allRequests.value.map(request => normalizeRequestType(request.type)))
		return [
			{key: 'all', name: 'All'},
			...Object.entries(REQUEST_TYPES)
				.filter(([key]) => requestTypesOfSession.has(key))
				.map(([key, name]) => ({key, name})),
		]
	})

	// filtering requests
	// 1. filter out invalid timestamps
	const requestsWithValidTimestamps = computed(() => {
		if (!allRequests.value) return []
		let requests = [...allRequests.value]
		// Hide network requests that contain invalid NaN:NaN timestamps, but only in sessions uploaded by Bird versions <= v1.58.0.
		// Afterwards, the issue is fixed by avoiding custom bird event naming collisions, that occur while recording via SDK and IR in tandem.
		// For reference:
		// - the linear ticket https://linear.app/birdeatsbug/issue/BEB-2213/network-logs-recorded-from-sdk-are-broken
		// - the commits where the custom events are renamed: 72fa550f5327cef914cb4ae60e401fd6a44c90e6 and aa532ddbd7411713731e54b4503ad5e042479e55
		if (compareSemverVersionNumbers(sessionStore.session?.browserExtensionVersion, '1.58.0') <= 0) {
			requests = requests.filter(request => !isNaN(parseInt(request.millisecondsSinceSessionStart)))
		}
		return requests
	})

	// 2. filter out requests that don't match the active request type filter
	const requestsOfCorrectType = computed(() => {
		const requests = requestsWithValidTimestamps.value
		const hasRequestTypeFilter = filterOptions.value.requestType !== 'all'
		if (!hasRequestTypeFilter) return requests
		return requests.filter(request => normalizeRequestType(request.type) === filterOptions.value.requestType)
	})

	// 3. filter out requests that don't match the show/hide options
	const filteredRequests = computed(() => {
		return requestsOfCorrectType.value.filter(request => {
			if (filterOptions.value.errorsOnly && !request.error && request.statusCode < 400) {
				return false
			}
			const url = request.url
			if (!url) return true

			if (filterOptions.value.thirdPartyRequests) {
				const sessionHostname = sessionStore.session.hostname
				if (url.startsWith('http') && new URL(url).hostname === sessionHostname) {
					return false
				}
			}
			if (filterOptions.value.hideDataUrls && (url.startsWith('data:') || url.startsWith('blob:'))) {
				return false
			}
			if (filterOptions.value.hideExtensionUrls && url.startsWith('chrome-extension://')) {
				return false
			}
			return true
		})
	})

	// 4. filter out requests that don't match the search filter
	const displayedRequests = computed(() => {
		if (!filterOptions.value.search) return filteredRequests.value
		return filteredRequests.value.filter(passesSearchFilter)
	})

	function passesSearchFilter(request) {
		// go through each search term
		const shouldPassFilter = filterOptions.value.searchTerms.every(term => {
			const shouldInvertTerm = term.startsWith('-')
			const termToCheck = shouldInvertTerm ? term.slice(1) : term
			// if term is empty, skip it
			if (!termToCheck.trim()) return true

			if (shouldInvertTerm || filterOptions.value.invert) {
				// the term cannot be present in any of the columns
				return displayedColumnsRef.value.every(({key}) => {
					if (key === 'start') return true
					if (key === 'status') key = 'statusCode'
					const value = `${request[key]}`.toLowerCase()
					if (value.includes(termToCheck)) return false
					return true
				})
			}
			// should not invert, so it should be present in at least one of the columns
			return displayedColumnsRef.value.some(({key}) => {
				if (key === 'start') return false
				if (key === 'status') key = 'statusCode'
				const value = `${request[key]}`.toLowerCase()
				if (value.includes(termToCheck)) return true
				return false
			})
		})

		return shouldPassFilter
	}

	function setRequestTypeFilter(requestType) {
		filterOptions.value.requestType = requestType
		if (localStorage) localStorage.networkRequestTypeFilter = filterOptions.value.requestType
	}

	function ensureActiveRequestTypeFilterExists() {
		// make sure that the request type filter is set to a request type which exists for this session
		if (!allRequests.value?.length) return
		if (!requestTypeFilters.value.map(({key}) => key).includes(filterOptions.value.requestType)) {
			filterOptions.value.requestType = 'all'
		}
	}

	watch(requestTypeFilters, ensureActiveRequestTypeFilterExists, {immediate: true})

	const isAnyCollapsibleFilterActive = computed(() => {
		return (
			filterOptions.value.hideDataUrls ||
			filterOptions.value.hideExtensionUrls ||
			filterOptions.value.errorsOnly ||
			filterOptions.value.thirdPartyRequests ||
			filterOptions.value.requestType !== 'all'
		)
	})

	function resetFilters() {
		filterOptions.value.search = ''
		filterOptions.value.searchTerms = []
		filterOptions.value.invert = false
		filterOptions.value.hideDataUrls = false
		filterOptions.value.hideExtensionUrls = false
		filterOptions.value.errorsOnly = false
		filterOptions.value.thirdPartyRequests = false
		setRequestTypeFilter('all')
	}

	return {
		requestTypeFilters,
		displayedRequests,
		filterOptions,
		filteredRequests,
		isAnyCollapsibleFilterActive,
		setRequestTypeFilter,
		resetFilters,
	}
}
